// Components
import CarouselModal, { CarouselData } from "components/table/carousel/carousel-modal"
import CarouselTrigger from "components/table/carousel/carousel-trigger"
import { TextEdit } from "components/table/inputs"
import { field as _field } from "components/table/lib/column-field"
import SearchHeader from "components/table/search-header"
import {
  focusFirstCellByRowIndex, getFocusedRowIndex, getPagingInfo, pageToFirst, TableAutomation, unfocusAllCells
} from "components/table/table-automation"
// Controls
import Controls from "components/user-admin/controls/"
// Styles
import { UserGroupTable_s } from "components/user-admin/user-styles"
import automationService from "data/automation"
import carouselService from "data/carousel"
// Data
import api_match from "data/interface/api"
import keyService, * as chord from "data/key"
import { canPaste, copy, paste } from "data/lib/copy-paste"
import { handleReferences } from "data/lib/handle-references"
import portfolioService from "data/portfolio"
import usersService from "data/user"
import userGroupService from "data/user-groups"
import _ from "lodash"
import { Column } from "primereact/column"
import { ContextMenu } from "primereact/contextmenu"
// PR
import { DataTable } from "primereact/datatable"
import React, { useEffect, useRef, useState } from "react"

type ContextRow = {
  usergroupid: number
  code: string
  systeminput?: string
}

const UserGroupTable = (props: {tableId: string, data: object[]}) => {
  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const traceMenuItem = {
    label: 'Trace', icon: 'pi pi-fw pi-search', command: e=> {
      const refreshFn = () => userGroupService.fetchUserGroups()
      handleReferences('usergroup', contextRow.usergroupid, {refreshFn});
    }
  }

  const nonSystemMenu = [
    {label: 'Copy Cell', icon: 'pi pi-fw pi-copy', command: e => { if(contextRow.systeminput === "systemImmutable") return;
      copy(contextCell, contextRow)
    }},
    {label: 'Delete Row', icon: 'pi pi-fw pi-trash', command: e => { if(contextRow.systeminput) return;
      const pager         = getPagingInfo(props.tableId);
      const currentRowNum = getFocusedRowIndex() + 1
      const currentRecordIndex = (pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1
      showDeleteConfirmation(contextRow, currentRecordIndex + 1)
    }},
    traceMenuItem
  ]

  const systemMenu = [{...traceMenuItem}]

  const [carouselData, updateCarouselData] = useState<CarouselData|null>(null);
  const [searchFilter, setSearchFilter] = useState<string>("")
  const [contextRow, setContextRow] = useState<ContextRow>({usergroupid: 0, code: ''})
  const [contextCell, setContextCell] = useState<object|null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<JSX.Element | null>(null)
  const [_portfolios, setPortfolios] = useState<object[]>([])
  const [users, setUsers] = useState<object[]>([])
  const menuRef = useRef<ContextMenu | null>(null)
  const [menu, setMenu] = useState<[]|object[]>(nonSystemMenu)
  const prevSearch = usePrevious(searchFilter);
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const field = _field('usergroup')

  useEffect(() => {
    const carouselSubscription = carouselService.stream().subscribe(data => {
      updateCarouselData(data);
    })

    const portfolioSubscription = portfolioService.stream().subscribe(data => {
      api_match(
        data,
        init  => setPortfolios(init.value),
        ok    => {
          setPortfolios(ok.value)
        },
        err   => {
          setPortfolios(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const usersSubscription = usersService.stream().subscribe(data => {
      let users = data['users'].map(user => { return user });
      setUsers(users);
    })

    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_SHIFT_PLUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            unfocusAllCells()
            userGroupService.create()
          }
          break
        case k.chord === chord.CTRL_SHIFT_MINUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            const pager         = getPagingInfo(props.tableId);
            const currentRowNum = getFocusedRowIndex() + 1
            const currentRecordIndex = (pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1
            const records       = tableRef.current.props.value!;
            const currentRow    = records[currentRecordIndex];
            if (!currentRow.systeminput) showDeleteConfirmation({usergroupid: currentRow.usergroupid, code: currentRow.code}, currentRecordIndex + 1)
          }
          break
      }
    })

    portfolioService.fetchPortfolios()
    usersService.getUsers()

    return function cleanup() {
      carouselSubscription.unsubscribe()
      portfolioSubscription.unsubscribe()
      usersSubscription.unsubscribe()
      keySubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    const userGroupSubscription = userGroupService.eventStream().subscribe(e => {
      switch(true) {
        case e.type === "USERGROUP_CREATED":
          let rowNum = _.filter(props.data, x => _.startsWith(x.code, '*')).length
          setTimeout(() => pageToFirst(props.tableId), 1)
          setTimeout(() => {
            focusFirstCellByRowIndex(props.tableId, rowNum)
          }, 500)
          break
      }
    })

    return () => userGroupSubscription.unsubscribe()
  }, [props.data.length])

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, 'current.filter', '', 'contains');
    else if (searchFilter !== "") _.invoke(tableRef, 'current.filter', searchFilter, 'contains');
    else return
  }, [searchFilter])

  useEffect(() => {
    !contextRow.systeminput || (contextRow.systeminput && !contextRow.systeminput.startsWith("system")) ? setMenu(nonSystemMenu) : setMenu(systemMenu)
  }, [contextRow])

  const persist = (value, field, rowData) => {
    let userGroup = _.cloneDeep(rowData);
    _.set(userGroup, field, value);
    userGroupService.patch(userGroup);
    if (value) carouselService.updateRowData(userGroup);
  }

  useEffect(() => {
    if(canPaste(contextCell)) {
      nonSystemMenu.splice(1, 0, {label: 'Paste Cell', icon: 'pi pi-fw pi-file', command: e => { if(contextRow.systeminput) return;
        paste(contextCell, contextRow, () => userGroupService.fetchUserGroups(), 'usergroup')
      }})
    }
    contextRow.systeminput === "systemImmutable" ? setMenu(systemMenu) : setMenu(nonSystemMenu)
  }, [contextCell])

  const showDeleteConfirmation = (record: ContextRow, currentRow: number) => {
    automationService.broadcast("MODAL_OPENING", {})
    setDeleteConfirmation(Controls.DeleteConfirmation({
      show: true,
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        userGroupService.deleteOne(record.usergroupid)
        setDeleteConfirmation(null)
        setTimeout(() => {
          focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0)
        }, 300)
      },
      rowNum: currentRow,
      code: record.code
    }))
  }

  function updateContextMenu(originalEvent){
    let systemRows = document.querySelectorAll(".system-row, .systemImmutable-row");
    for (let i of systemRows){
      if (originalEvent.target === i || i.contains(originalEvent.target)) {
        setMenu(systemMenu);
      } else {
        if (!menu.length){
          setMenu(nonSystemMenu)
        }
      }
    }
    _.invoke(menuRef.current, "show", originalEvent)
  }


  const rowClassName = (rowData) => {
    if (rowData.systeminput && rowData.systeminput === "system"){
      return {"system-row" : rowData.systeminput === "system"}
    }
    else if (rowData.systeminput && rowData.systeminput === "systemImmutable"){
      return {"systemImmutable-row" : rowData.systeminput === "systemImmutable"}
    }
    else return {"" : rowData}
  }

  const sortFn = ({field, order}) => userGroupService.sortFn(props.data, field, order == 1 ? 'asc' : 'desc')

  return (
    <div>
      <SearchHeader setSearchFilter={setSearchFilter} />
      <ContextMenu model={menu} ref={menuRef} appendTo={document.querySelector("body")} onHide={() => {}}/>
      <UserGroupTable_s id={`${props.tableId}-scrollable-container`}>
        <DataTable
          globalFilter={searchFilter}
          id={props.tableId}
          ref={tableRef}
          value={props.data}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          frozenWidth="45px"
          paginatorPosition="bottom"
          rowClassName={rowClassName}
          onContextMenu={e => menuRef.current ? updateContextMenu(e.originalEvent) : null}
          contextMenuSelection={contextRow}
          onContextMenuSelectionChange={e => {
            setContextRow(e.value)
            setContextCell(e.originalEvent.target)
          }}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
        >
          <Column header="#" frozen={true} style={{minWidth: "45px"}}
            body={(rowData, props) => props.rowIndex + 1}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column system-disabled"
            filter
            header="Code"
            {...field('code')}
            editor={props => TextEdit({persist, ...props})}
            sortable
            sortFunction={sortFn}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column system-disabled"
            filter
            header="Name"
            {...field('name')}
            editor={props => TextEdit({persist, ...props})}
            sortable
            sortFunction={sortFn}
          />
          <Column style={{minWidth: "180px"}} className="p-editable-column system-disabled"
            header="Users"
            {...field('users', (rowData, props) => { return(
              rowData.systeminput === 'systemImmutable' ? <span style={{paddingLeft: 20}}>System</span> :
              <CarouselTrigger
                field="users"
                rowData={rowData}
                title="Users"
                persist={persist}
                controls={{single: {control: Controls.Users}, name: 'Select Users'}}
                hover={{
                  detailed: {list: {label: 'Users', key: 'users'}}
                }}
                namedItems={{users}}
                componentAtRest={()=> {
                  if (rowData['users']?.length > 1){
                    return 'Detailed'
                  } else if (rowData['users']?.length > 0) {
                    let user = _.find(users, {'userid': rowData['users'][0]})
                    return user ? user['name'] : ''
                  } else {
                    return 'Select Users'
                  }
                }}
              />)},
              (err) => !_.includes(['code', 'name'], err.path[0])
            )}
          />
        </DataTable>
        { carouselData && carouselData['show'] && <CarouselModal code="code" data={carouselData} key="carousel-modal" width={492}/> }
        { deleteConfirmation }
        { props.data.length ? <TableAutomation tableId={props.tableId}/> : null }
      </UserGroupTable_s>
    </div>
  )
}

export default UserGroupTable
